import { useEffect, useRef, useState } from 'react';
import { addDays, dayStart, isRecord, ms, readRoute, type Doc } from '@day';
import type { Model } from '../data/model';
import { searchQuery, usableGeocode } from '../data/find';
import { googlePlace, lookup, LookupError, stubbed } from '../data/maps';
import { FRESH_MS, GOOGLE_MS, googleUntilOf } from '../data/waiting';
import { Icon } from './Icon';

/**
 * Finding an address as it's typed, wherever one is asked for (Plan a route, Add a stop, where routes start): places
 * from the business's own routes first (free), then a lookup through Routr's server once typing pauses, never a click:
 * Geocoding first, the place search only when that finds nothing usable, as the phone orders them.
 */

export interface Place {
  address: string;
  area: string;
  lat: number;
  lng: number;
  placeId?: string;
  /** When Google's position has to go (29 days from Google's answer): wherever the place goes, it goes too. */
  googleUntil?: number | null;
  name?: string;
  houseNumber?: string;
  precision?: 'exact' | 'low';
  /** Found through Google just now: only then is what was typed kept as the address the owner wrote. */
  google?: boolean;
}

/** Addresses from the business's routes of the last 29 days, with Google's position still on them: each carries its
 * route's date (`googleUntil`, from the earliest of its positions), and a route past it is left out. */
export function recentPlaces(m: Model): Place[] {
  const out = new Map<string, Place>();
  const since = dayStart(addDays(m.today, -28), m.zone);
  const docs = [...(m.data.history ?? new Map()).entries(), ...m.data.routes.entries()];
  for (const [id, raw] of docs) {
    const r = readRoute(id, raw, m.zone);
    if (!r) continue;
    const at = r.startedAt ?? r.sentAt ?? 0;
    if (at < since) continue;
    const until = ms((raw as Doc).googleUntil) ?? at + GOOGLE_MS;
    if (until <= m.now) continue;
    for (const s of r.stops) {
      if (!s.spot || s.rough || s.handover || out.has(s.address)) continue;
      out.set(s.address, { address: s.address, area: s.area, lat: s.spot.lat, lng: s.spot.lng, placeId: s.placeId, googleUntil: until });
    }
  }
  return [...out.values()];
}

export const firstPart = (a: string) => a.split(',')[0].trim();

/** One of a lookup's places; a suggestion has no position until it's picked (`pid` is its place ID). */
export type LookedPlace = Place & { pid?: string };

/** A lookup's answer: Geocoding's one place, or the place search's suggestions (placed when picked). */
export interface Looked {
  q: string;
  places: LookedPlace[];
  via: 'geocode' | 'places' | null;
  error?: string;
}

/** An answer already had, and when Google gave it. */
export type Asked = Map<string, { at: number; looked: Looked }>;

/** Answers already had, by country and what was typed: the same query isn't asked twice in a day. */
const asked: Asked = new Map();

/** [key]'s answer in [cache] while it's under a day old. Older answers go, and Google's positions in them: a tab can
 * stay open for weeks. */
export function recall(cache: Asked, key: string, now: number): Looked | null {
  for (const [k, v] of cache) if (now - v.at >= FRESH_MS) cache.delete(k);
  return cache.get(key)?.looked ?? null;
}

/**
 * What was typed, looked up once typing pauses (no click). A newer query's answer always wins; an older one arriving
 * late is dropped. [near]: the shop, to rule out a namesake far away.
 */
export function useLookup(text: string, on: boolean, country: string | undefined, session: React.MutableRefObject<string>, near?: { lat: number; lng: number } | null): Looked | 'looking' | null {
  const [got, setGot] = useState<Looked | 'looking' | null>(null);
  const seq = useRef(0);
  useEffect(() => {
    const n = ++seq.current;
    if (!on) return setGot(null);
    const key = `${country ?? ''}|${text.toLowerCase()}`;
    const had = recall(asked, key, Date.now());
    if (had) return setGot(had);
    setGot('looking');
    const t = setTimeout(async () => {
      let out: Looked;
      try {
        out = { q: text, via: null, places: [] };
        if (!stubbed) {
          const g = await lookup('geocode', { address: searchQuery(text).slice(0, 300), ...(country ? { components: `country:${country}`, region: country } : {}), language: 'en' });
          const r = Array.isArray(g.results) ? (g.results[0] as Doc | undefined) : undefined;
          const gp = r && isRecord(r) ? googlePlace(r, true) : null;
          if (gp && r && r.partial_match !== true && usableGeocode(gp, text, near)) out = { q: text, via: 'geocode', places: [{ ...gp, address: gp.address || text, area: gp.address.split(',')[1]?.trim() ?? '', google: true, googleUntil: googleUntilOf(Date.now()) }] };
        }
        if (!out.places.length) {
          const a = await lookup('autocomplete', { input: searchQuery(text).slice(0, 200), sessiontoken: session.current, ...(country ? { components: `country:${country}` } : {}), language: 'en' });
          const preds = (Array.isArray(a.predictions) ? a.predictions : []) as Doc[];
          out = { q: text, via: 'places', places: preds.map((p) => ({ address: String(p.description ?? ''), area: String((p.structured_formatting as Doc | undefined)?.secondary_text ?? '').split(',')[0], lat: NaN, lng: NaN, pid: String(p.place_id) })) };
        }
        asked.set(key, { at: Date.now(), looked: out });
      } catch (e) {
        out = { q: text, via: null, places: [], error: e instanceof LookupError ? e.message : 'Routr’s server didn’t answer. Check the connection and try again.' };
      }
      if (n === seq.current) setGot(out);
    }, 450);
    return () => clearTimeout(t);
  }, [text, on, country]);
  return got;
}

/** A looked-up place on the map: a suggestion is placed now, in its search session. A string says why it couldn't be. */
export async function placeOf(p: LookedPlace, session: string): Promise<Place | string> {
  if (Number.isFinite(p.lat)) return p;
  try {
    const d = await lookup('details', { place_id: p.pid, sessiontoken: session });
    const gp = isRecord(d.result) ? googlePlace(d.result, false) : null;
    if (!gp) return 'Google had no position for that place. Try the address another way.';
    return { ...gp, address: gp.address || p.address, area: p.area, placeId: gp.placeId ?? p.pid, google: true, googleUntil: googleUntilOf(Date.now()) };
  } catch (e) {
    return e instanceof LookupError ? e.message : 'Routr’s server didn’t answer.';
  }
}

/** What a lookup has so far: that it's looking, then its places. Enter takes the first. */
export function LookupRows({ text, found, placing, onPick }: { text: string; found: Looked | 'looking' | null; placing?: boolean; onPick: (p: LookedPlace) => void }) {
  const look = found && found !== 'looking' && found.q === text ? found : null;
  return (
    <>
      {found === 'looking' && (
        <div className="empty cap" aria-busy="true">
          Looking up “{text}”…{stubbed ? ' (emulators: a fixed list of Abuja places answers)' : ''}
        </div>
      )}
      {placing && <div className="empty cap">Placing it…</div>}
      {look?.places.map((p, i) => (
        <div key={p.address + (p.pid ?? '')} className={`item${i === 0 ? ' hot' : ''}`} role="button" tabIndex={0} onClick={() => onPick(p)}>
          <Icon name="mapPin" />
          <div className="grow">
            <b>{firstPart(p.address)}</b>
            <div className="cap">{p.address.split(',').slice(1, 3).join(',').trim()} · {look.via === 'geocode' ? 'Google’s address' : 'from Google'}</div>
          </div>
          {i === 0 ? <span className="kbd">↵</span> : <Icon name="plus" className="ink3" />}
        </div>
      ))}
    </>
  );
}
